import React from 'react';
import { ChatEvent } from 'libs/end-to-end/src/chat/chat-events';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';
import { jsonParse } from 'libs/end-to-end/src/utils/utility/json-parse';

type ChatSubscriber = (event: ChatEvent) => void;

type GlobalConnection = {
  eventSource: EventSource;
  subscribers: Set<ChatSubscriber>;
  lastEventId?: string;
};

export function useChatEventBridge() {
  const connectionRef = React.useRef<GlobalConnection | null>(null);

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  const ensureConnection = (): GlobalConnection => {
    if (
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId
    ) {
      throw new Error('Chat bridge not ready');
    }

    if (connectionRef.current) {
      return connectionRef.current;
    }

    const params = new URLSearchParams();

    // ✅ ALWAYS resume if possible
    const lastEventId = localStorage.getItem('chat:lastEventId') ?? undefined;

    if (lastEventId) {
      params.set('lastEventId', lastEventId);
    }

    const url =
      `${getApiBaseUrl()}/organizations/${selectedOrganizationId}` +
      `/databases/${selectedDatabaseId}` +
      `/revisions/${selectedDatabaseRevisionId}` +
      `/chats/stream` +
      (params.toString() ? `?${params.toString()}` : '');

    const es = new EventSource(url, {
      withCredentials: true,
    });

    const connection: GlobalConnection = {
      eventSource: es,
      subscribers: new Set(),
      lastEventId: undefined,
    };

    es.onmessage = (message) => {
      if (typeof message.data !== 'string') {
        console.error('Invalid SSE payload', message.data);
        return;
      }

      const parsed = jsonParse(message.data, ChatEvent);

      if (parsed.resultType === 'err') {
        console.error('Failed to parse chat event', parsed.error);
        return;
      }

      // ✅ persist cursor globally (not per chat)
      if (message.lastEventId) {
        connection.lastEventId = message.lastEventId;

        localStorage.setItem('chat:lastEventId', message.lastEventId);
      }

      // 🔥 fan-out to ALL subscribers
      connection.subscribers.forEach((sub) => {
        sub(parsed.value);
      });
    };

    es.onerror = (err) => {
      console.error('Chat SSE error', err);
      // browser will auto-reconnect
    };

    connectionRef.current = connection;

    return connection;
  };

  const subscribeToChat = (
    chatId: string,
    cb: ChatSubscriber,
  ): (() => void) => {
    const connection = ensureConnection();

    // 🔥 wrap subscriber with filtering
    const wrapped: ChatSubscriber = (event) => {
      if (event.chatId === chatId) {
        cb(event);
      }
    };

    connection.subscribers.add(wrapped);

    return () => {
      connection.subscribers.delete(wrapped);

      // ❌ DO NOT close EventSource
      // this is global and long-lived
    };
  };

  return { subscribeToChat };
}
